/**
 * A registry of every mic/camera stream this tab has opened.
 *
 * Media is acquired in a dozen places — the shared mic manager, the recorder,
 * the camera hook, the peer connection, and inside third-party SDKs we don't
 * control (the ElevenLabs conversation opens its own LiveKit mic track). Each
 * has its own teardown, and on the abandon paths a single missed one is enough
 * to leave the browser's recording indicator lit and the hardware held for the
 * life of the tab.
 *
 * Wrapping getUserMedia is the only vantage point that sees the streams we
 * didn't acquire ourselves, which is what makes an exit able to guarantee the
 * mic and camera are actually off rather than hoping every owner cooperated.
 *
 * This is a backstop, not a replacement: hooks must still tear down their own
 * media so sessions end cleanly (the ElevenLabs conversation in particular is
 * billed per minute and has to be ended, not merely muted).
 */

interface PatchedMediaDevices extends MediaDevices {
  __trackRegistryInstalled?: boolean;
}

const liveStreams = new Set<MediaStream>();

const isDead = (stream: MediaStream): boolean =>
  stream.getTracks().every((t) => t.readyState === 'ended');

function track(stream: MediaStream): void {
  liveStreams.add(stream);
  for (const t of stream.getTracks()) {
    t.addEventListener('ended', () => {
      if (isDead(stream)) liveStreams.delete(stream);
    });
  }
}

/** Call once at startup, before anything can request media. */
export function installMediaTrackRegistry(): void {
  const devices = navigator?.mediaDevices as PatchedMediaDevices | undefined;
  if (!devices?.getUserMedia || devices.__trackRegistryInstalled) return;

  const original = devices.getUserMedia.bind(devices);
  devices.getUserMedia = async (constraints?: MediaStreamConstraints) => {
    const stream = await original(constraints);
    try {
      track(stream);
    } catch {
      // Registry problems must never break media acquisition.
    }
    return stream;
  };
  devices.__trackRegistryInstalled = true;
}

/** Labels of the tracks still live, for diagnosing which owner leaked. */
export function describeLiveMedia(): string[] {
  const out: string[] = [];
  for (const stream of liveStreams) {
    for (const t of stream.getTracks()) {
      if (t.readyState === 'live') out.push(`${t.kind}:${t.label || 'unlabelled'}`);
    }
  }
  return out;
}

/**
 * Stop every tracked track that is still live. Returns how many were stopped.
 *
 * Only for leaving a session — it is indiscriminate by design, so never call it
 * while any part of the app still needs the mic or camera.
 */
export function stopAllTrackedMedia(reason: string): number {
  let stopped = 0;
  for (const stream of liveStreams) {
    for (const t of stream.getTracks()) {
      if (t.readyState === 'ended') continue;
      try {
        t.stop();
        stopped++;
      } catch (err) {
        console.warn('[MediaRegistry] Failed to stop track:', err);
      }
    }
    liveStreams.delete(stream);
  }
  if (stopped > 0) {
    console.log(`[MediaRegistry] Stopped ${stopped} leftover track(s) on ${reason}`);
  }
  return stopped;
}
